import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Upload,
  message,
  Modal,
  Tooltip,
  Empty,
  Spin,
  Divider,
  Typography,
} from 'antd';
import {
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  UploadOutlined,
  FilePdfOutlined,
  ReloadOutlined,
  DeleteOutlined,
  SafetyCertificateOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import apiService, { Lead } from '../../services/api';
import VodafoneDocumentWizard from '../VodafoneDocumentWizard';
import NOSDocumentWizard from '../NOSDocumentWizard';
import NOSSecuritasDocumentWizard from '../NOSSecuritasDocumentWizard';
import ProsegurForm from '../ProsegurForm';
import AlticeResidencialForm from '../AlticeResidencialForm';
import AlticeSaleDetailView from '../AlticeSaleDetailView';
import {
  workspaceSurface,
  workspaceCardHead,
  workspaceCardBody,
  workspaceTableWrap,
  workspaceInset,
} from './workspaceTheme';

const { Text } = Typography;

const PdfToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 4px;
`;

const PdfActionBtn = styled.button<{ $accent: string }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 6px 16px 6px 8px;
  border-radius: 12px;
  border: 1px solid ${(p) => `${p.$accent}33`};
  background: linear-gradient(165deg, #ffffff 0%, ${(p) => `${p.$accent}0d`} 55%, #fafbfc 100%);
  color: #141824;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  line-height: 1.2;
  letter-spacing: -0.01em;
  cursor: pointer;
  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.05),
    0 0 0 1px rgba(255, 255, 255, 0.6) inset;
  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    border-color 0.18s ease,
    background 0.18s ease;

  &:hover {
    border-color: ${(p) => p.$accent};
    box-shadow:
      0 6px 20px ${(p) => `${p.$accent}2e`},
      0 0 0 1px rgba(255, 255, 255, 0.7) inset;
    transform: translateY(-2px);
    background: linear-gradient(165deg, #ffffff 0%, ${(p) => `${p.$accent}18`} 100%);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  }

  &:focus-visible {
    outline: 2px solid ${(p) => p.$accent};
    outline-offset: 2px;
  }
`;

const PdfIconWell = styled.span<{ $accent: string }>`
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => `${p.$accent}1f`};
  color: ${(p) => p.$accent};
  font-size: 17px;
`;

interface DocumentRow {
  id: number;
  source: 'generated' | 'uploaded' | 'altice';
  name: string;
  created_at: string;
  status: string;
  meta?: string;
  raw: any;
  /** MIME for blob / download hint */
  mimeType: string;
}

const INVALID_FILENAME_CHARS = /[/\\?%*:|"<>]/g;

function sanitizeDownloadSegment(value: string, maxLen = 100): string {
  return (value || 'ficheiro')
    .replace(INVALID_FILENAME_CHARS, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLen);
}

function hasFileExtension(filename: string): boolean {
  return /\.[a-zA-Z0-9]{2,8}$/.test((filename || '').trim());
}

function extensionFromMime(mime: string | undefined): string {
  if (!mime) return '.bin';
  const lower = mime.toLowerCase().split(';')[0].trim();
  const map: Record<string, string> = {
    'application/pdf': '.pdf',
    'image/png': '.png',
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/webp': '.webp',
    'image/gif': '.gif',
  };
  if (map[lower]) return map[lower];
  const slash = lower.indexOf('/');
  if (slash > 0 && lower.length < 48) {
    const sub = lower.slice(slash + 1).replace('+xml', '');
    if (/^[a-z0-9.-]+$/i.test(sub)) return `.${sub.split('.').pop() || sub}`;
  }
  return '.bin';
}

function formatUploadedFileMeta(stage: string | undefined, fieldName: string | undefined): string {
  const st = (stage || '').trim();
  const fn = (fieldName || '').trim();
  if (fn.startsWith('altice_sale_')) {
    return [st || 'MEO', 'Documentação MEO'].filter(Boolean).join(' · ');
  }
  const slot =
    fn.startsWith('workspace_doc_') || fn === 'workspace_upload' ? 'Documento carregado' : fn;
  return [st, slot].filter(Boolean).join(' · ');
}

function formatAlticeSaleName(sale: Record<string, any>): string {
  const label = (sale.nome || sale.pacote_id || `Venda #${sale.id}`).toString().trim();
  return `MEO Residencial — ${label}`;
}

function formatAlticeSaleMeta(sale: Record<string, any>): string {
  const parts = [
    sale.pacote_id,
    sale.contribuinte,
    sale.segmento,
  ]
    .map((v) => (v || '').toString().trim())
    .filter(Boolean);
  return parts.length ? parts.join(' · ') : 'Formulário MEO';
}

function buildDownloadFilename(row: DocumentRow, leadDisplayName: string): string {
  const leadPart = sanitizeDownloadSegment(leadDisplayName, 80);
  if (row.source === 'uploaded') {
    const base = sanitizeDownloadSegment(row.name, 180);
    if (hasFileExtension(base)) return `${leadPart}_${base}`;
    return `${leadPart}_${base}${extensionFromMime(row.mimeType)}`;
  }
  if (row.source === 'altice') {
    const docPart = sanitizeDownloadSegment(row.name, 120);
    return `${leadPart}_${docPart}.json`;
  }
  const docPart = sanitizeDownloadSegment(row.name, 120);
  return `${leadPart}_${docPart}.pdf`;
}

interface DocumentsHubProps {
  lead: Lead;
  /** Strip outer Card; use inside a parent shell (e.g. unified ficha). */
  embedded?: boolean;
}

const formatDate = (s: string) => {
  if (!s) return '';
  try {
    return new Date(s).toLocaleDateString('pt-PT');
  } catch {
    return s;
  }
};

const DocumentsHub: React.FC<DocumentsHubProps> = ({ lead, embedded = false }) => {
  const [docs, setDocs] = useState<DocumentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [vodafoneOpen, setVodafoneOpen] = useState(false);
  const [nosOpen, setNosOpen] = useState(false);
  const [nosSecuritasOpen, setNosSecuritasOpen] = useState(false);
  const [prosegurOpen, setProsegurOpen] = useState(false);
  const [alticeOpen, setAlticeOpen] = useState(false);

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewBase64, setPreviewBase64] = useState<string>('');
  const [alticePreviewSale, setAlticePreviewSale] = useState<Record<string, any> | null>(null);
  const [alticePreviewLoading, setAlticePreviewLoading] = useState(false);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const [docsResp, filesResp, alticeSales] = await Promise.all([
        apiService.getLeadDocuments(lead.id),
        apiService.getLeadFiles(lead.id),
        apiService.getAlticeSalesByLead(lead.id).catch(() => [] as any[]),
      ]);

      const out: DocumentRow[] = [];
      if (docsResp.success && docsResp.documents) {
        for (const d of docsResp.documents as any[]) {
          out.push({
            id: d.id,
            source: 'generated',
            name: d.document_type || 'Documento',
            created_at: d.created_at,
            status: d.is_expired
              ? 'Expirado'
              : d.days_until_expiry !== null && d.days_until_expiry <= 3
              ? `Expira em ${d.days_until_expiry}d`
              : 'Válido',
            meta: 'Gerado',
            raw: d,
            mimeType: 'application/pdf',
          });
        }
      }
      if (filesResp.success && filesResp.files) {
        for (const f of filesResp.files as any[]) {
          out.push({
            id: f.id,
            source: 'uploaded',
            name: f.file_name || f.filename || 'Ficheiro',
            created_at: f.uploaded_at,
            status: 'Carregado',
            meta: formatUploadedFileMeta(f.stage, f.field_name),
            raw: f,
            mimeType: (f.file_type as string) || 'application/octet-stream',
          });
        }
      }
      for (const sale of alticeSales as any[]) {
        out.push({
          id: sale.id,
          source: 'altice',
          name: formatAlticeSaleName(sale),
          created_at: sale.created_at,
          status: 'Submetido',
          meta: formatAlticeSaleMeta(sale),
          raw: sale,
          mimeType: 'application/json',
        });
      }
      out.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setDocs(out);
    } catch (e) {
      message.error('Erro ao carregar documentos.');
    } finally {
      setLoading(false);
    }
  }, [lead.id]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const handlePreview = async (row: DocumentRow) => {
    if (row.source === 'altice') {
      setAlticePreviewSale(row.raw);
      setAlticePreviewLoading(true);
      try {
        const full = await apiService.getAlticeSale(row.id);
        setAlticePreviewSale(full);
      } catch {
        message.warning('A mostrar dados em cache — não foi possível recarregar o formulário.');
      } finally {
        setAlticePreviewLoading(false);
      }
      return;
    }
    setPreviewTitle(row.name);
    setPreviewBase64('');
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    setPreviewVisible(true);
    setPreviewLoading(true);
    try {
      let response;
      if (row.source === 'generated') {
        response = await apiService.previewDocument(row.id);
      } else {
        response = await apiService.previewLeadFile(lead.id, row.id);
      }
      if (response.success && 'pdf_data' in response) {
        const data = response.pdf_data as string;
        const maxInline = 5 * 1024 * 1024;
        if (data.length > maxInline) {
          const bin = atob(data);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          const blob = new Blob([arr], { type: 'application/pdf' });
          setPreviewBlobUrl(URL.createObjectURL(blob));
        } else {
          setPreviewBase64(data);
        }
      } else if (!response.success && 'error' in response) {
        message.error(response.error);
        setPreviewVisible(false);
      } else {
        message.error('Erro ao carregar pré-visualização.');
        setPreviewVisible(false);
      }
    } catch (e) {
      message.error('Erro ao carregar pré-visualização.');
      setPreviewVisible(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (row: DocumentRow) => {
    try {
      if (row.source === 'altice') {
        const blob = new Blob([JSON.stringify(row.raw, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = buildDownloadFilename(row, lead.name || `lead_${lead.id}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        message.success('Descarregado.');
        return;
      }
      const resp =
        row.source === 'generated'
          ? await apiService.downloadDocument(row.id)
          : await apiService.downloadLeadFile(lead.id, row.id);
      if (resp.success) {
        const mime =
          row.source === 'generated'
            ? 'application/pdf'
            : row.mimeType || resp.data.type || 'application/octet-stream';
        const data = resp.data;
        const blob =
          data instanceof Blob
            ? data.type && data.type !== 'application/octet-stream'
              ? data
              : new Blob([await data.arrayBuffer()], { type: mime })
            : new Blob([data], { type: mime });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = buildDownloadFilename(row, lead.name || `lead_${lead.id}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        message.success('Descarregado.');
      } else {
        message.error('Erro ao descarregar.');
      }
    } catch (e) {
      message.error('Erro ao descarregar.');
    }
  };

  const handleDelete = (row: DocumentRow) => {
    if (row.source === 'generated') {
      message.info('Documentos gerados não podem ser eliminados.');
      return;
    }
    if (row.source === 'altice') {
      Modal.confirm({
        title: 'Eliminar formulário MEO',
        content: `Eliminar definitivamente "${row.name}"?`,
        okText: 'Eliminar',
        okType: 'danger',
        cancelText: 'Cancelar',
        onOk: async () => {
          try {
            await apiService.deleteAlticeSale(row.id);
            message.success('Formulário MEO eliminado.');
            refresh();
          } catch {
            message.error('Erro ao eliminar.');
          }
        },
      });
      return;
    }
    if (row.source !== 'uploaded') {
      return;
    }
    Modal.confirm({
      title: 'Eliminar ficheiro',
      content: `Eliminar definitivamente "${row.name}"?`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const r = await apiService.deleteLeadFile(lead.id, row.id);
          if (r.success) {
            message.success('Eliminado.');
            refresh();
          } else {
            message.error('Erro ao eliminar.');
          }
        } catch {
          message.error('Erro ao eliminar.');
        }
      },
    });
  };

  const handleUpload = async (file: File) => {
    if (file.size > 20 * 1024 * 1024) {
      message.error('O ficheiro deve ser menor que 20MB.');
      return false;
    }
    const formData = new FormData();
    formData.append('file', file);
    formData.append('stage', lead.section || 'registro');
    // Unique slot per upload: backend replaces rows with the same (lead_id, stage, field_name).
    const uniqueSuffix = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    formData.append('field_name', `workspace_doc_${uniqueSuffix}`.slice(0, 100));
    try {
      const resp = await apiService.uploadLeadFile(lead.id, formData);
      if (resp.success) {
        message.success('Ficheiro carregado.');
        refresh();
      } else {
        message.error(resp.message || 'Erro ao carregar.');
      }
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      message.error(data?.error || data?.message || 'Erro ao carregar.');
    }
    return false;
  };

  const columns = [
    {
      title: 'Documento',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, row: DocumentRow) => (
        <Space>
          <FileTextOutlined />
          <div>
            <div>{text}</div>
            {row.meta ? (
              <div style={{ fontSize: 12, color: '#888' }}>{row.meta}</div>
            ) : null}
          </div>
        </Space>
      ),
    },
    {
      title: 'Origem',
      key: 'source',
      width: 120,
      render: (_: unknown, row: DocumentRow) => (
        <Tag
          color={
            row.source === 'generated' ? 'geekblue' : row.source === 'altice' ? 'orange' : 'green'
          }
        >
          {row.source === 'generated' ? 'Gerado' : row.source === 'altice' ? 'MEO' : 'Carregado'}
        </Tag>
      ),
    },
    {
      title: 'Data',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (s: string) => formatDate(s),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (s: string, row: DocumentRow) => (
        <Tag
          color={
            row.source === 'generated'
              ? s === 'Expirado'
                ? 'red'
                : 'green'
              : row.source === 'altice'
              ? 'orange'
              : 'blue'
          }
        >
          {s}
        </Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 220,
      render: (_: any, row: DocumentRow) => (
        <Space>
          <Tooltip title={row.source === 'altice' ? 'Ver detalhes do formulário' : 'Pré-visualizar'}>
            <Button size="small" icon={<EyeOutlined />} onClick={() => handlePreview(row)} />
          </Tooltip>
          <Tooltip title="Descarregar">
            <Button size="small" icon={<DownloadOutlined />} onClick={() => handleDownload(row)} />
          </Tooltip>
          {row.source === 'uploaded' || row.source === 'altice' ? (
            <Tooltip title="Eliminar">
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(row)}
              />
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
  ];

  const hubExtra = (
    <Space wrap>
      <Tooltip title="Atualizar lista">
        <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading} />
      </Tooltip>
      <Upload
        multiple
        showUploadList={false}
        beforeUpload={handleUpload}
        accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
      >
        <Button type="primary" icon={<UploadOutlined />}>
          Carregar ficheiro
        </Button>
      </Upload>
    </Space>
  );

  const hubMain = (
    <>
      <div
        style={{
          background: 'rgba(30, 58, 95, 0.05)',
          borderRadius: 10,
          border: '1px solid rgba(15, 23, 42, 0.07)',
          padding: '4px 12px 12px',
          marginBottom: 16,
        }}
      >
        <Divider orientation="left" plain style={{ marginTop: 8 }}>
          <Text strong style={{ color: '#0f172a' }}>
            Criar PDFs ou solicitar proposta
          </Text>
        </Divider>
        <PdfToolbar>
          <PdfActionBtn type="button" $accent="#e60000" onClick={() => setVodafoneOpen(true)}>
            <PdfIconWell $accent="#e60000">
              <FilePdfOutlined />
            </PdfIconWell>
            Vodafone
          </PdfActionBtn>
          <PdfActionBtn type="button" $accent="#00a651" onClick={() => setNosOpen(true)}>
            <PdfIconWell $accent="#00a651">
              <FilePdfOutlined />
            </PdfIconWell>
            NOS
          </PdfActionBtn>
          <PdfActionBtn type="button" $accent="#6d28d9" onClick={() => setNosSecuritasOpen(true)}>
            <PdfIconWell $accent="#6d28d9">
              <SafetyCertificateOutlined />
            </PdfIconWell>
            NOS Securitas
          </PdfActionBtn>
          <PdfActionBtn type="button" $accent="#15803d" onClick={() => setProsegurOpen(true)}>
            <PdfIconWell $accent="#15803d">
              <FilePdfOutlined />
            </PdfIconWell>
            Prosegur
          </PdfActionBtn>
          <PdfActionBtn type="button" $accent="#ea580c" onClick={() => setAlticeOpen(true)}>
            <PdfIconWell $accent="#ea580c">
              <HomeOutlined />
            </PdfIconWell>
            MEO
          </PdfActionBtn>
        </PdfToolbar>
      </div>
      <Divider orientation="left" plain style={{ borderColor: 'rgba(15, 23, 42, 0.08)' }}>
        <Text strong style={{ color: '#0f172a' }}>
          Ficheiros associados
        </Text>
      </Divider>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : docs.length === 0 ? (
        <Empty description="Ainda sem ficheiros na lista. Use os botões acima para gerar PDFs, submeter MEO ou carregar documentos." />
      ) : (
        <div style={workspaceTableWrap}>
          <Table<DocumentRow>
            columns={columns as any}
            dataSource={docs}
            rowKey={(r) => `${r.source}-${r.id}`}
            size="small"
            pagination={false}
          />
        </div>
      )}
    </>
  );

  const hubDialogs = (
    <>
      <VodafoneDocumentWizard
        open={vodafoneOpen}
        lead={lead}
        onClose={() => setVodafoneOpen(false)}
        onSave={() => {
          message.success('Documentos Vodafone guardados.');
          refresh();
        }}
      />
      <NOSDocumentWizard
        open={nosOpen}
        lead={lead}
        onClose={() => setNosOpen(false)}
        onSave={() => {
          message.success('Documentos NOS guardados.');
          refresh();
        }}
      />
      <NOSSecuritasDocumentWizard
        open={nosSecuritasOpen}
        lead={lead}
        onClose={() => setNosSecuritasOpen(false)}
        onSave={() => {
          message.success('Documentos NOS Securitas guardados.');
          refresh();
        }}
      />
      <ProsegurForm
        open={prosegurOpen}
        lead={lead}
        onClose={() => setProsegurOpen(false)}
        onSave={() => {
          message.success('Dados Prosegur guardados.');
          refresh();
        }}
      />
      <Modal
        title={`MEO — ${lead.name}`}
        open={alticeOpen}
        onCancel={() => setAlticeOpen(false)}
        footer={null}
        width={1200}
        style={{ top: 20 }}
        destroyOnClose
      >
        <AlticeResidencialForm
          lead={{
            id: lead.id,
            name: lead.name,
            email: lead.email,
            phone: lead.phone,
            address: lead.address,
            city: lead.city,
            postal_code: lead.postal_code,
            dif: lead.dif,
            section: lead.section,
          }}
          onClose={() => setAlticeOpen(false)}
          onSave={() => {
            refresh();
          }}
        />
      </Modal>

      <Modal
        open={!!alticePreviewSale}
        title={`Formulário MEO — ${alticePreviewSale?.nome || alticePreviewSale?.pacote_id || `#${alticePreviewSale?.id || ''}`}`}
        onCancel={() => setAlticePreviewSale(null)}
        footer={null}
        width={1200}
        style={{ top: 20 }}
        destroyOnClose
      >
        {alticePreviewLoading ? (
          <div style={{ textAlign: 'center', padding: 64 }}>
            <Spin size="large" />
          </div>
        ) : alticePreviewSale ? (
          <AlticeSaleDetailView
            sale={alticePreviewSale}
            maxHeight="75vh"
            showSystemInfo
            leadName={lead.name}
          />
        ) : null}
      </Modal>

      <Modal
        open={previewVisible}
        title={`Pré-visualização — ${previewTitle}`}
        onCancel={() => {
          setPreviewVisible(false);
          if (previewBlobUrl) {
            URL.revokeObjectURL(previewBlobUrl);
            setPreviewBlobUrl(null);
          }
        }}
        footer={null}
        width={820}
        destroyOnClose
      >
        {previewLoading ? (
          <div style={{ textAlign: 'center', padding: 64 }}>
            <Spin size="large" />
          </div>
        ) : previewBlobUrl ? (
          <iframe
            title="preview"
            src={previewBlobUrl}
            width="100%"
            height="600px"
            style={{ border: 'none' }}
          />
        ) : previewBase64 ? (
          <iframe
            title="preview"
            src={`data:application/pdf;base64,${previewBase64}`}
            width="100%"
            height="600px"
            style={{ border: 'none' }}
          />
        ) : (
          <Empty description="Sem dados para mostrar." />
        )}
      </Modal>
    </>
  );

  if (embedded) {
    return (
      <>
        <div
          style={{
            ...workspaceInset,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            padding: '10px 16px',
          }}
        >
          <Text strong>Documentos do Cliente</Text>
          {hubExtra}
        </div>
        <div style={{ ...workspaceCardBody, padding: '16px 20px 20px' }}>{hubMain}</div>
        {hubDialogs}
      </>
    );
  }

  return (
    <>
      <Card
        title="Documentos do Cliente"
        style={workspaceSurface}
        headStyle={workspaceCardHead}
        bodyStyle={{ ...workspaceCardBody, padding: '16px 20px 20px' }}
        extra={hubExtra}
      >
        {hubMain}
      </Card>
      {hubDialogs}
    </>
  );
};

export default DocumentsHub;
